import React, { useEffect, useState } from 'react';
import { ExoPDFViewer } from './ExoPDFViewer';
import ExoDialog from './ExoDialog';
import Loader from '../special/Loader';
import { DocRender } from 'react-doc-render';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import XRechnungPreview from '../special/XRechnungPreview';
import { isXRechnungXml } from '../../utils/xrechnung';

// Office document MIME types
const OFFICE_MIME_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'application/rtf',
];

export const isOfficeMimeType = (mimeType) => {
  return OFFICE_MIME_TYPES.includes(mimeType);
};

const getExtension = (src) =>
  (src || '').split('?')[0].split('#')[0].split('.').pop().toLowerCase();

export const isXmlSource = (src, mimeType = '') => {
  const type = mimeType.toLowerCase();
  return (
    type === 'text/xml' ||
    type === 'application/xml' ||
    type.endsWith('+xml') ||
    getExtension(src) === 'xml'
  );
};

const TEXT_EXTENSIONS = ['txt', 'md', 'log', 'json', 'ini', 'yml', 'yaml'];
export const isTextSource = (src, mimeType = '') => {
  const type = mimeType.toLowerCase();
  return (
    type === 'text/plain' ||
    type === 'text/markdown' ||
    type === 'application/json' ||
    TEXT_EXTENSIONS.includes(getExtension(src))
  );
};

// Text / XML preview: XRechnung / ZUGFeRD XML is visualized,
// any other text-based file is shown as plain text
export const TextPreview = ({ src }) => {
  const { t } = useTranslation();
  const [xml, setXml] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setXml(null);
    setError(false);
    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((text) => !cancelled && setXml(text))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (error) return <Typography>{t('Cant load File')}</Typography>;
  if (xml === null) return <Loader />;
  if (isXRechnungXml(xml)) return <XRechnungPreview xml={xml} />;
  return (
    <Box
      component="pre"
      className="w-full h-full overflow-auto text-sm p-4 m-0 whitespace-pre-wrap break-words font-mono"
    >
      {xml}
    </Box>
  );
};

export const ExoFilePreview = ({ open, src, onClose, mimeType }) => {
  useEffect(() => {
    checkImageValidity();
  }, [src, open]);

  const [isValidUrl, setIsValidUrl] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  const checkImageValidity = () => {
    if (!open) return;

    // Skip image validation for Office documents and XML
    if (
      isOfficeMimeType(mimeType) ||
      isXmlSource(src, mimeType) ||
      isTextSource(src, mimeType)
    ) {
      setIsValidUrl(true);
      setIsChecking(false);
      return;
    }

    const img = new Image();

    img.onload = () => {
      setIsValidUrl(true);
      setIsChecking(false);
    };

    img.onerror = () => {
      setIsValidUrl(false);
      setIsChecking(false);
    };

    img.src = src;
  };

  function handleClose() {
    src = null;
    setIsValidUrl(false);
    setIsChecking(true);
    onClose();
  }

  if (open && (isXmlSource(src, mimeType) || isTextSource(src, mimeType))) {
    return (
      <ExoDialog open={open} onClose={handleClose}>
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TextPreview src={src} />
        </Box>
      </ExoDialog>
    );
  }

  // Render Office documents with react-doc-render
  if (open && isOfficeMimeType(mimeType)) {
    return (
      <ExoDialog open={open} onClose={handleClose}>
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DocRender
            uri={src}
            mime={mimeType}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
      </ExoDialog>
    );
  }

  return (
    <>
      {open &&
        (mimeType === 'application/pdf' ? (
          <ExoPDFViewer open={open} onClose={handleClose} src={src} />
        ) : (
          <ExoDialog open={open} onClose={handleClose}>
            {isChecking && <Loader />}
            {mimeType.split('/')[0] == 'video' ||
            mimeType == 'application/octet-stream' ? (
              <video
                autoplay={'autoplay'}
                src={src}
                alt="preview"
                className={`w-full h-full object-contain ${
                  isValidUrl && isChecking ? 'hidden' : 'block'
                }`}
              />
            ) : (
              (isChecking && <Loader />,
              (
                <img
                  src={src}
                  alt="preview"
                  className={`w-full h-full object-contain ${
                    isValidUrl && isChecking ? 'hidden' : 'block'
                  }`}
                />
              ))
            )}
          </ExoDialog>
        ))}
    </>
  );
};
